import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Trash2, Download, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

// Debug event types with emojis for easy identification
const DEBUG_EVENTS = {
  REFERRAL_SIGNUP: { emoji: '🎯', color: 'bg-blue-500', label: 'Referral Signup' },
  ADD_SONGS: { emoji: '🎵', color: 'bg-green-500', label: 'Add Songs' },
  GO_LIVE: { emoji: '🔴', color: 'bg-red-500', label: 'Go Live' },
  RECEIVE_REQUEST: { emoji: '📨', color: 'bg-purple-500', label: 'Receive Request' },
  GO_OFFLINE: { emoji: '⚫', color: 'bg-gray-500', label: 'Go Offline' },
  ERROR: { emoji: '❌', color: 'bg-red-600', label: 'Error' },
  SUCCESS: { emoji: '✅', color: 'bg-green-600', label: 'Success' },
  INFO: { emoji: 'ℹ️', color: 'bg-blue-600', label: 'Info' }
};

// Global debug log storage
let debugLogs = [];
let debugListeners = [];

// Debug logging functions
export const debugLog = (eventType, message, data = null) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    id: Date.now() + Math.random(),
    timestamp,
    eventType,
    message,
    data: data ? JSON.stringify(data, null, 2) : null,
    type: DEBUG_EVENTS[eventType] || DEBUG_EVENTS.INFO
  };
  
  debugLogs.push(logEntry);
  
  // Keep only last 100 logs to prevent memory issues
  if (debugLogs.length > 100) {
    debugLogs = debugLogs.slice(-100);
  }
  
  // Notify listeners
  debugListeners.forEach(listener => listener(logEntry));
  
  // Also log to console for development
  console.log(`🐛 DEBUG [${eventType}]:`, message, data || '');
};

export const clearDebugLogs = () => {
  debugLogs = [];
  debugListeners.forEach(listener => listener(null));
};

export const addDebugListener = (listener) => {
  debugListeners.push(listener);
  return () => {
    debugListeners = debugListeners.filter(l => l !== listener);
  };
};

const DebugConsole = () => {
  const [logs, setLogs] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollAreaRef = useRef(null);
  const { toast } = useToast();

  // Hide debug console in production or on deployed sites
  const isProduction = import.meta.env.PROD || 
                      window.location.hostname.includes('web.app') || 
                      window.location.hostname.includes('firebaseapp.com') ||
                      window.location.hostname !== 'localhost';

  useEffect(() => {
    // Don't initialize if in production
    if (isProduction) return;
    
    // Initialize with existing logs
    setLogs([...debugLogs]);
    
    // Add listener for new logs
    const removeListener = addDebugListener((newLog) => {
      if (newLog) {
        setLogs(prev => [...prev, newLog]);
      } else {
        setLogs([]);
      }
    });

    return removeListener;
  }, [isProduction]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const handleClear = () => {
    clearDebugLogs();
    toast({
      title: "Debug logs cleared",
      description: "All debug logs have been removed."
    });
  };

  const handleCopy = () => {
    const logText = logs.map(log => 
      `[${log.timestamp}] ${log.type.emoji} ${log.type.label}: ${log.message}${log.data ? '\n' + log.data : ''}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(logText).then(() => {
      toast({
        title: "Debug logs copied",
        description: "All debug logs have been copied to clipboard."
      });
    });
  };

  const handleDownload = () => {
    const logData = {
      timestamp: new Date().toISOString(),
      logs: logs.map(log => ({
        timestamp: log.timestamp,
        eventType: log.eventType,
        message: log.message,
        data: log.data ? JSON.parse(log.data) : null
      }))
    };
    
    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Debug logs downloaded",
      description: "Debug logs have been saved as JSON file."
    });
  };

  const getEventCounts = () => {
    const counts = {};
    logs.forEach(log => {
      counts[log.eventType] = (counts[log.eventType] || 0) + 1;
    });
    return counts;
  };

  const eventCounts = getEventCounts();

  // Don't render anything if in production
  if (isProduction) {
    return null;
  }

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsVisible(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg"
          size="sm"
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-h-[600px]">
      <Card className="bg-white shadow-xl border-2 border-blue-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800">
              🐛 Debug Console
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => setAutoScroll(!autoScroll)}
                variant="outline"
                size="sm"
                className={autoScroll ? "bg-green-100" : ""}
              >
                {autoScroll ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              </Button>
              <Button
                onClick={() => setIsVisible(false)}
                variant="outline"
                size="sm"
              >
                <EyeOff className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          {/* Event Count Badges */}
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.entries(eventCounts).map(([eventType, count]) => {
              const eventInfo = DEBUG_EVENTS[eventType] || DEBUG_EVENTS.INFO;
              return (
                <Badge key={eventType} className={`${eventInfo.color} text-white text-xs`}>
                  {eventInfo.emoji} {count}
                </Badge>
              );
            })}
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          {/* Action Buttons */}
          <div className="flex gap-2 mb-3">
            <Button onClick={handleCopy} variant="outline" size="sm" className="flex-1">
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
            <Button onClick={handleDownload} variant="outline" size="sm" className="flex-1">
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
            <Button onClick={handleClear} variant="outline" size="sm" className="flex-1">
              <Trash2 className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
          
          {/* Logs Display */}
          <div ref={scrollAreaRef} className="h-64 border rounded-md bg-gray-50 overflow-y-auto">
            <div className="p-3 space-y-2">
              {logs.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <div className="text-4xl mb-2">🔍</div>
                  <div>No debug logs yet</div>
                  <div className="text-sm">Start the referral flow to see logs</div>
                </div>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className="bg-white p-2 rounded border-l-4 border-l-blue-400 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{log.type.emoji}</span>
                      <span className="font-medium text-sm">{log.type.label}</span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 mb-1">{log.message}</div>
                    {log.data && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                          View Data
                        </summary>
                        <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                          {log.data}
                        </pre>
                      </details>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div className="text-xs text-gray-500 mt-2 text-center">
            {logs.length} log{logs.length !== 1 ? 's' : ''} • Auto-scroll {autoScroll ? 'ON' : 'OFF'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DebugConsole;
